import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AddJsxChange, UndoChange } from "@/store/zustand/changeLedgerTypes";
import {
  useDesignCommandManager,
  useEditingClassesByCodebaseId,
} from "@/store/zustand/designCommandManagerStore";
import { getElementsForCodebaseId } from "../utils/domUtils";
import { TempoElement } from "@/utils/tempoElement";
import {
  createDummyComponent,
  getInstantUpdateHtmlForStoryboard,
} from "@/components/AppHeader/utils";
import { useActiveProject } from "@/utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useLayoutAndTypography } from "@/components/AppHeader/hooks/useLayoutAndTypography";
import { CursorType, setCursorType } from "@/store/globalSettingsSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { setSelectedElementKey } from "@/store/iframeSlice";
import { OutlineSystemRef } from "../../OutlineSystem";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import {
  DEFAULT_STORYBOARD_TEXT_HEIGHT,
  DEFAULT_STORYBOARD_TEXT_WIDTH,
} from "@/components/Canvas/utils";
import { findElementByDomUniquePath } from "@/components/Canvas/utils/utils";
import { NavTreeNode } from "@/utils/types";
import { isDarkColor } from "@/components/RightStyle/TailwindStylingPanel/utils/utils";
import { updateCodebaseId } from "../utils/utils";

interface DrawDivState {
  parentElementKey: string | null;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  preMappingCodebaseId: string | null;
  divCodebaseId: string | null;
  initialChange: AddJsxChange | null;
}

export const useDrawDivInStoryboard = (
  iframeDocRef: React.RefObject<Document>,
  storyboardId: string,
  outlineSystemRef: React.RefObject<OutlineSystemRef>,
) => {
  const [drawState, setDrawState] = useState<DrawDivState>({
    parentElementKey: null,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    preMappingCodebaseId: null,
    divCodebaseId: null,
    initialChange: null,
  });

  const storyboardIdRef = useRef(storyboardId);
  useEffect(() => {
    storyboardIdRef.current = storyboardId;
  }, [storyboardId]);

  const isDrawingRef = useRef(false);

  const dispatch = useDispatch();

  const project = useActiveProject();
  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const {
    selectedLayoutOptionToUse,
    selectedTypographyOptionToUse,
    usingTailwind,
  } = useLayoutAndTypography();

  const selectedLayoutOptionToUseRef = useRef(selectedLayoutOptionToUse);
  useEffect(() => {
    selectedLayoutOptionToUseRef.current = selectedLayoutOptionToUse;
  }, [selectedLayoutOptionToUse]);
  const selectedTypographyOptionToUseRef = useRef(
    selectedTypographyOptionToUse,
  );
  useEffect(() => {
    selectedTypographyOptionToUseRef.current = selectedTypographyOptionToUse;
  }, [selectedTypographyOptionToUse]);
  const usingTailwindRef = useRef(usingTailwind);
  useEffect(() => {
    usingTailwindRef.current = usingTailwind;
  }, [usingTailwind]);

  const drawStateRef = useRef(drawState);
  useEffect(() => {
    drawStateRef.current = drawState;
  }, [drawState]);

  const createTextDiv = useCallback(
    (parentElementKey: string, startX: number, startY: number) => {
      if (!iframeDocRef.current) {
        return;
      }

      // Parse the parent element key to get the codebase ID
      const parentElement = TempoElement.fromKey(parentElementKey!);
      const parentCodebaseId = parentElement.codebaseId;

      const textCodebaseId = `tempo-${uuidv4()}`;

      const parentNavTreeNode =
        useCanvasStore.getState().elementKeyToNavNodeLookup[parentElementKey!];

      // Find the color that is displayed for the parent element to determine if we should use dark text
      let parentColor: string | null = null;
      let searchNavTreeNode: NavTreeNode | undefined = parentNavTreeNode;
      while (searchNavTreeNode && !parentColor) {
        const elementForParent = findElementByDomUniquePath(
          iframeDocRef.current,
          searchNavTreeNode.domUniquePath ??
            searchNavTreeNode.domUniquePathForComponent,
        );

        if (elementForParent) {
          const computedStyle =
            getComputedStyle(elementForParent).backgroundColor;
          // If it is not transparent or some default color, use it
          if (computedStyle !== "rgba(0, 0, 0, 0)") {
            parentColor = computedStyle;
          }
        }

        searchNavTreeNode = searchNavTreeNode.parent;
      }

      const isDarkText = parentColor ? !isDarkColor(parentColor) : true;

      // Use the existing getInstantUpdateHtmlForStoryboard function to get the proper HTML and JSX
      // We use the currently selected layout option (usually blank for drawing divs)
      const { instantUpdateHtml, jsxContents } =
        getInstantUpdateHtmlForStoryboard(
          true, // Text html
          usingTailwindRef.current,
          selectedTypographyOptionToUseRef.current,
          selectedLayoutOptionToUseRef.current,
          projectRef.current,
          DEFAULT_STORYBOARD_TEXT_WIDTH,
          DEFAULT_STORYBOARD_TEXT_HEIGHT,
          textCodebaseId,
          isDarkText, // Dark text
        );

      const dummyComponentContents = createDummyComponent(
        jsxContents,
        projectRef?.current?.framework,
      );

      const dropPosition = useCanvasStore.getState().dropPosition ?? {};
      const { beforeCodebaseId, afterCodebaseId } = dropPosition;

      // Clear the drop position after using it
      useCanvasStore.getState().setDropPosition({});

      // Create the final AddJsxChange
      const finalChange = new AddJsxChange({
        codebaseIdToAddTo: parentCodebaseId,
        fileContentsToSourceFrom: dummyComponentContents,
        htmlForInstantUpdate: instantUpdateHtml,
        editTextAfterAdd: true,
        codebaseIdHashingKey: uuidv4(),
        beforeCodebaseId,
        afterCodebaseId,
      });

      const tempoElement = new TempoElement(
        textCodebaseId,
        storyboardIdRef.current,
        `${parentElement.uniquePath}-${parentNavTreeNode?.children?.length}`,
      );

      finalChange.setSelectedElementsAfterInstantUpdate(
        tempoElement.getKey(),
        [],
      );

      // Send the final change (not as instant, but as a real change)
      useDesignCommandManager.getState().executeCommand(finalChange);

      dispatch(
        setCursorType({
          cursorType: CursorType.Pointer,
        }),
      );
    },
    [iframeDocRef, dispatch],
  );

  const startDrawDiv = useCallback(
    (parentElementKey: string, startX: number, startY: number) => {
      if (isDrawingRef.current) {
        return;
      }

      isDrawingRef.current = true;

      useIframePoolStore
        .getState()
        .setIgnoreUpdatesForStoryboards(storyboardIdRef.current);

      // Parse the parent element key to get the codebase ID
      const parentElement = TempoElement.fromKey(parentElementKey);
      const parentCodebaseId = parentElement.codebaseId;

      const codebaseIdHashingKey = uuidv4();

      // Generate a unique ID for this div
      // The ID from the html will automatically get mapped with the unique ID, so the div codebase id
      // will actually be the mapped ID
      const preMappingCodebaseId = `tempo-${uuidv4()}`;
      const divCodebaseId = updateCodebaseId(
        preMappingCodebaseId,
        codebaseIdHashingKey,
      );

      // Create the initial div with minimal size
      // TODO: Implement absolute mode
      // const absoluteInitialHTML = `<div data-tempoelementid="${divCodebaseId}" style="position: absolute; left: ${startX}px; top: ${startY}px; width: 1px; height: 1px; background-color: white; border: 1px solid rgb(59, 130, 246); pointer-events: none;"></div>`;
      const { instantUpdateHtml } = getInstantUpdateHtmlForStoryboard(
        false, // isCursorText - we're drawing layout, not text
        usingTailwindRef.current,
        selectedTypographyOptionToUseRef.current,
        selectedLayoutOptionToUseRef.current,
        projectRef.current,
        1,
        1,
        preMappingCodebaseId,
      );

      const dropPosition = useCanvasStore.getState().dropPosition ?? {};
      const { beforeCodebaseId, afterCodebaseId } = dropPosition;

      // Clear the drop position after using it
      useCanvasStore.getState().setDropPosition({});

      // Create the AddJsxChange for the initial creation
      const initialChange = new AddJsxChange({
        codebaseIdToAddTo: parentCodebaseId,
        htmlForInstantUpdate: instantUpdateHtml,
        beforeCodebaseId,
        afterCodebaseId,
        codebaseIdHashingKey,
      });

      const parentNavTreeNode =
        useCanvasStore.getState().elementKeyToNavNodeLookup[parentElementKey!];

      const tempoElement = new TempoElement(
        divCodebaseId,
        storyboardIdRef.current,
        `${parentElement.uniquePath}-${parentNavTreeNode?.children?.length}`,
      );

      dispatch(setSelectedElementKey(tempoElement.getKey()));

      // Send the instant change to create the div
      useDesignCommandManager.getState().sendInstantChange(initialChange);

      // Update state
      setDrawState({
        parentElementKey,
        startX,
        startY,
        currentX: startX,
        currentY: startY,
        preMappingCodebaseId,
        divCodebaseId,
        initialChange,
      });

      outlineSystemRef.current?.refreshOutlines();
    },
    [dispatch, outlineSystemRef],
  );

  const updateDivSize = useCallback(
    (currentX: number, currentY: number) => {
      if (!isDrawingRef.current || !drawStateRef.current.divCodebaseId) {
        return;
      }

      if (!iframeDocRef.current) {
        return;
      }

      // Calculate dimensions
      const width = Math.abs(currentX - drawStateRef.current.startX);
      const height = Math.abs(currentY - drawStateRef.current.startY);
      const left = Math.min(currentX, drawStateRef.current.startX);
      const top = Math.min(currentY, drawStateRef.current.startY);

      // Find the div element in the iframe
      const elements = getElementsForCodebaseId(
        iframeDocRef.current,
        drawStateRef.current.divCodebaseId,
      );

      if (elements.length > 0) {
        const divElement = elements[0] as HTMLElement;

        // Update the div's size directly
        divElement.style.width = `${width}px`;
        divElement.style.height = `${height}px`;
        divElement.style.left = `${left}px`;
        divElement.style.top = `${top}px`;
      }

      outlineSystemRef.current?.refreshOutlines();

      // Update current position in state
      setDrawState((prev) => ({
        ...prev,
        currentX,
        currentY,
      }));
    },
    [iframeDocRef, outlineSystemRef],
  );

  const finishDrawDiv = useCallback(() => {
    useIframePoolStore
      .getState()
      .unsetIgnoreUpdatesForStoryboards(storyboardIdRef.current);

    if (!isDrawingRef.current || !drawStateRef.current.divCodebaseId) {
      return;
    }

    const {
      startX,
      startY,
      currentX,
      currentY,
      parentElementKey,
      preMappingCodebaseId,
      divCodebaseId,
      initialChange,
    } = drawStateRef.current;

    // Parse the parent element key to get the codebase ID
    const parentElement = TempoElement.fromKey(parentElementKey!);
    const parentCodebaseId = parentElement.codebaseId;

    // Calculate final dimensions
    let width = Math.abs(currentX - startX);
    let height = Math.abs(currentY - startY);

    if (width <= 1 && height <= 1) {
      // Based on some very minial testin from figma
      width = 80;
      height = 80;
    }

    // Delete the old div using an undo
    useDesignCommandManager.getState().sendInstantChange(
      new UndoChange({
        changeToUndo: initialChange!,
      }),
    );

    // Use the existing getInstantUpdateHtmlForStoryboard function to get the proper HTML and JSX
    // We use the currently selected layout option (usually blank for drawing divs)
    const { instantUpdateHtml, jsxContents } =
      getInstantUpdateHtmlForStoryboard(
        false, // isCursorText - we're drawing layout, not text
        usingTailwindRef.current,
        selectedTypographyOptionToUseRef.current,
        selectedLayoutOptionToUseRef.current,
        projectRef.current,
        width,
        height,
        preMappingCodebaseId!,
      );

    const dummyComponentContents = createDummyComponent(
      jsxContents,
      projectRef?.current?.framework,
    );

    // Create the final AddJsxChange
    const finalChange = new AddJsxChange({
      codebaseIdToAddTo: parentCodebaseId,
      fileContentsToSourceFrom: dummyComponentContents,
      htmlForInstantUpdate: instantUpdateHtml,
      codebaseIdHashingKey: initialChange!.changeFields.codebaseIdHashingKey,
      beforeCodebaseId: initialChange!.changeFields.beforeCodebaseId,
      afterCodebaseId: initialChange!.changeFields.afterCodebaseId,
    });

    const parentNavTreeNode =
      useCanvasStore.getState().elementKeyToNavNodeLookup[parentElementKey!];

    const tempoElement = new TempoElement(
      divCodebaseId,
      storyboardIdRef.current,
      `${parentElement.uniquePath}-${parentNavTreeNode?.children?.length}`,
    );

    finalChange.setSelectedElementsAfterInstantUpdate(
      tempoElement.getKey(),
      [],
    );

    // Send the final change (not as instant, but as a real change)
    useDesignCommandManager.getState().executeCommand(finalChange);

    // Reset state
    setDrawState({
      parentElementKey: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      preMappingCodebaseId: null,
      divCodebaseId: null,
      initialChange: null,
    });

    dispatch(
      setCursorType({
        cursorType: CursorType.Pointer,
      }),
    );

    isDrawingRef.current = false;
  }, [dispatch, storyboardIdRef]);

  return {
    startDrawDiv,
    createTextDiv,
    updateDivSize,
    finishDrawDiv,
    isDrawingRef,
  };
};
